import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    Renderer2
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {MatButtonModule} from '@angular/material/button';
import {MatCardModule} from '@angular/material/card';
import {MatIconModule} from '@angular/material/icon';
import {MatTooltipModule} from '@angular/material/tooltip';
import {DestinationType} from '../../destinations.types';
import {finalize, Subject, takeUntil} from 'rxjs';
import {RestFactoryService} from '../../../../../core/services/rest-factory.service';
import {FuseConfirmationService} from '../../../../../../@fuse/services/confirmation';
import {NgxSpinnerService} from 'ngx-spinner';
import {environment} from "../../../../../../environments/environment";

@Component({
    selector: 'app-destination-main-media',
    templateUrl: './destination-main-media.component.html',
    styleUrls: ['./destination-main-media.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        MatCardModule,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule
    ]
})
export class DestinationMainMediaComponent implements OnInit, OnDestroy {
    @Input() destination: DestinationType;
    @Input() reviewMode: boolean = false;

    userUiType: number = environment.user_ui_type || 1;

    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private API_POST_MAIN_IMAGE = "/admin/destination/destinationmainimageadd";
    private API_POST_MAIN_VIDEO = "/admin/destination/destinationmainvideoadd";
    private API_DELETE_MAIN_VIDEO = "/admin/destination/destinationmainvideoremove";
    private API_POST_MAIN_CARD_IMAGE = "/admin/destination/destinationcardimageadd";

    constructor(
        private _restFactoryService: RestFactoryService,
        private _fuseConfirmationService: FuseConfirmationService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _spinner: NgxSpinnerService,
        private _renderer: Renderer2,
        private _el: ElementRef
    ) {}

    ngOnInit(): void {
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    isMissingImage(val: any): boolean {
        if (val === null || val === undefined) {
            return true;
        }
        if (typeof val === 'string') {
            const trimmed = val.trim();
            if (trimmed.length === 0 || trimmed.endsWith('content/undefined')) {
                return true;
            }
        }
        return false;
    }

    onFileInputClick(fileUploadId: string): void {
        const fileInput = this._el.nativeElement.querySelector(`#${fileUploadId}`);
        this._renderer.selectRootElement(fileInput).click();
    }

    handleFileUpload(event: any, uploadType: 'main' | 'card' | 'video'): void {
        const file = (event.target as HTMLInputElement).files[0];
        if (!file) {
            (event.target as HTMLInputElement).value = '';
            return;
        }

        const allowedImageExt = ["jpg", "png", "jpeg", "gif", "webp"];
        const allowedVideoExt = ["mp4", "mov"];
        const fileExtension = file.name.toLowerCase().split('.').pop();

        if (uploadType === 'video') {
            if (!allowedVideoExt.includes(fileExtension)) {
                this.showError("Invalid video format. Please upload a .mp4 or .mov file.");
                (event.target as HTMLInputElement).value = '';
                return;
            }
        } else {
            if (!allowedImageExt.includes(fileExtension)) {
                this.showError("Invalid image format. Please upload a .jpg, .png, .jpeg, .gif, or .webp file.");
                (event.target as HTMLInputElement).value = '';
                return;
            }
        }

        this.mediaUploader(uploadType, file);
        (event.target as HTMLInputElement).value = '';
    }

    mediaUploader(source: 'main' | 'card' | 'video', file: File): void {
        const payload = new FormData();
        payload.append("im_image", file);
        payload.append("id_destination", this.destination.id_destination);

        let api = "";
        let property: keyof DestinationType;

        if (source === "main") {
            api = this.API_POST_MAIN_IMAGE;
            property = 'im_media';
        } else if (source === "card") {
            api = this.API_POST_MAIN_CARD_IMAGE;
            property = 'im_mediacard';
        } else if (source === "video") {
            api = this.API_POST_MAIN_VIDEO;
            property = 'im_mediavideo';
        }

        this._spinner.show('managementSpinner').then();
        this._restFactoryService.httpPostService(api, payload)
            .pipe(
                takeUntil(this._unsubscribeAll),
                finalize(() => this._spinner.hide('managementSpinner').then())
            )
            .subscribe(response => {
                (this.destination as any)[property] = response.im_media;
                this._changeDetectorRef.markForCheck();
            });
    }

    deleteVideo(): void {
        const payload = new FormData();
        payload.append("id_destination", this.destination.id_destination);

        this._restFactoryService.httpPostService(this.API_DELETE_MAIN_VIDEO, payload)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                this.destination.im_mediavideo = null;
                this._changeDetectorRef.markForCheck();
            });
    }

    isVideoUrl(url: string): boolean {
        if (!url || url.endsWith('undefined')) {
            return false;
        }
        return ['.mp4', '.avi', '.flv', '.mov', '.wmv', '.mkv'].some(ext => url.toLowerCase().endsWith(ext));
    }

    private showError(message: string): void {
        const dialog = this._fuseConfirmationService.open({
            title: "Error!",
            message: message,
            actions: {confirm: {show: false}, cancel: {show: false}},
            dismissible: false
        });
        setTimeout(() => dialog.close(), 2000);
    }

    openInNewTab(url: string): void {
        if (!url) {
            return;
        }
        const cleanUrl = url.replace('_tmb', '');
        window.open(cleanUrl, '_blank');
    }

}
